export const PUBLIC_BRANDING_META_NAME = "enkey-public-branding";

export type PublicBrandingSnapshot = {
  logoUrl: string;
  footerLogoUrl: string;
  headerLogoWidth: number;
  footerLogoWidth: number;
  logoIntrinsicWidth: number;
  logoIntrinsicHeight: number;
  footerLogoIntrinsicWidth: number;
  footerLogoIntrinsicHeight: number;
  copyrightText: string;
  facebookUrl: string | null;
  instagramUrl: string | null;
  linkedinUrl: string | null;
  xUrl: string | null;
  favicon16Url: string;
  favicon32Url: string;
  appleTouchIconUrl: string;
  pwaIcon192Url: string;
  pwaIcon512Url: string;
  pwaMaskable192Url: string;
  pwaMaskable512Url: string;
  updatedAt: string | null;
};

const DEFAULT_LOGO_DIMENSIONS = { width: 1000, height: 1000 };
const RESPONSIVE_CUSTOM_WIDTHS = [160, 320, 480] as const;

function positiveDimension(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : fallback;
}

function comparableAssetPath(value: string | undefined | null) {
  if (!value) return "";
  try {
    return new URL(value, "https://www.enkey.eu").pathname;
  } catch {
    return value;
  }
}

export function sameBrandAsset(
  left: string | undefined | null,
  right: string | undefined | null,
) {
  return comparableAssetPath(left) === comparableAssetPath(right);
}

export function readInitialPublicBranding(
  documentValue:
    | Pick<Document, "querySelector">
    | undefined = typeof document === "undefined" ? undefined : document,
): PublicBrandingSnapshot | null {
  const encoded = documentValue
    ?.querySelector<HTMLMetaElement>(
      `meta[name="${PUBLIC_BRANDING_META_NAME}"]`,
    )
    ?.getAttribute("content");
  if (!encoded) return null;
  try {
    return JSON.parse(decodeURIComponent(encoded)) as PublicBrandingSnapshot;
  } catch {
    return null;
  }
}

export function resolvePublicBranding(
  initial: PublicBrandingSnapshot | null,
  live: Partial<PublicBrandingSnapshot> | null | undefined,
): PublicBrandingSnapshot | null {
  if (!live) return initial;

  const logoUnchanged = sameBrandAsset(initial?.logoUrl, live.logoUrl);
  const footerLogoUnchanged = sameBrandAsset(
    initial?.footerLogoUrl,
    live.footerLogoUrl,
  );
  const merged = { ...initial, ...live } as PublicBrandingSnapshot;

  merged.logoIntrinsicWidth = logoUnchanged
    ? positiveDimension(
        initial?.logoIntrinsicWidth,
        DEFAULT_LOGO_DIMENSIONS.width,
      )
    : positiveDimension(live.logoIntrinsicWidth, DEFAULT_LOGO_DIMENSIONS.width);
  merged.logoIntrinsicHeight = logoUnchanged
    ? positiveDimension(
        initial?.logoIntrinsicHeight,
        DEFAULT_LOGO_DIMENSIONS.height,
      )
    : positiveDimension(
        live.logoIntrinsicHeight,
        DEFAULT_LOGO_DIMENSIONS.height,
      );
  merged.footerLogoIntrinsicWidth = footerLogoUnchanged
    ? positiveDimension(
        initial?.footerLogoIntrinsicWidth,
        merged.logoIntrinsicWidth,
      )
    : positiveDimension(
        live.footerLogoIntrinsicWidth,
        merged.logoIntrinsicWidth,
      );
  merged.footerLogoIntrinsicHeight = footerLogoUnchanged
    ? positiveDimension(
        initial?.footerLogoIntrinsicHeight,
        merged.logoIntrinsicHeight,
      )
    : positiveDimension(
        live.footerLogoIntrinsicHeight,
        merged.logoIntrinsicHeight,
      );

  if (logoUnchanged && initial?.logoUrl) merged.logoUrl = initial.logoUrl;
  if (footerLogoUnchanged && initial?.footerLogoUrl) {
    merged.footerLogoUrl = initial.footerLogoUrl;
  }
  return merged;
}

function mediaStorageId(src: string) {
  try {
    const pathname = new URL(src, "https://www.enkey.eu").pathname;
    return /^\/media\/([a-z0-9]+)$/i.exec(pathname)?.[1] ?? null;
  } catch {
    return null;
  }
}

export function brandLogoSourceSet(src: string, intrinsicWidth: number) {
  if (sameBrandAsset(src, "/brand/enkey-logo.png")) {
    return "/brand/enkey-logo-320.webp 320w, /brand/enkey-logo-640.webp 640w";
  }

  const storageId = mediaStorageId(src);
  if (!storageId) return null;
  const safeIntrinsicWidth = positiveDimension(intrinsicWidth, 0);
  const variants = RESPONSIVE_CUSTOM_WIDTHS.filter(
    (width) => width < safeIntrinsicWidth,
  ).map(
    (width) =>
      `/media/brand-logo/${encodeURIComponent(storageId)}/${width}.webp ${width}w`,
  );
  return variants.length ? variants.join(", ") : null;
}
